import { useLingui } from '@lingui/react/macro';
import { Info, Pencil, Trash2 } from 'lucide-react';
import { Notification } from 'modules/core/domain/interfaces/supabase/notifications/types';
import { NOTIFICATION_TYPE_LABELS, typeIconMap } from 'modules/notifications/domain/const';
import React from 'react';
import {
  NOTIFICATION_BODY_LAYOUT,
  NOTIFICATION_CARD_LAYOUT,
  NOTIFICATION_HEADER_LAYOUT,
  NOTIFICATION_TEXT_BLOCK,
} from './notification-layout';
import { typeToneMap } from './notification-type-tones';
import UDCard from 'ud-ui/components/card';
import UDIconTile from 'ud-ui/components/icon-tile';
import UDIconButton from 'ud-ui/components/icon-button';
import UDStatusBadge from 'ud-ui/components/status-badge';
import UDText from 'ud-ui/components/text';

interface Props {
  notification: Notification;
  isNew?: boolean;
  allowChange?: boolean;
  onEdit?: (n: Notification) => void;
  onDelete?: (id: number) => void;
}

/**
 * Одно уведомление — карточка: плитка типа, заголовок с бейджами («тип», «Новое»), дата и действия админа
 * в шапке, текст на утопленной подложке под ней. Цвет плитки задаёт тон типа, бейдж типа нейтральный, а
 * жёлтый бейдж «Новое» — единственное цветное пятно в строке.
 */
const NotificationComponent: React.FC<Props> = ({ notification, isNew, allowChange, onEdit, onDelete }) => {
  const { t, i18n } = useLingui();
  const typeLabel = NOTIFICATION_TYPE_LABELS[notification.type];
  const typeText = typeLabel ? t(typeLabel) : notification.type;
  const Icon = typeIconMap[notification.type] || Info;
  const date = new Date(notification.createdAt);
  const dateStr = date.toLocaleDateString(i18n.locale, { day: '2-digit', month: '2-digit', year: '2-digit' });
  const timeStr = date.toLocaleTimeString(i18n.locale, { hour: '2-digit', minute: '2-digit' });

  return (
    <UDCard as="article" className={NOTIFICATION_CARD_LAYOUT} data-testid="notification-card">
      <UDIconTile icon={<Icon size={20} />} tone={typeToneMap[notification.type] ?? 'neutral'} variant="solid" />
      <div className={NOTIFICATION_BODY_LAYOUT}>
        <div className={NOTIFICATION_HEADER_LAYOUT}>
          <UDText type="subhead" className="bold">
            {notification.title}
          </UDText>
          <UDStatusBadge tone="neutral">{typeText}</UDStatusBadge>
          {isNew && <UDStatusBadge tone="pending">{t`Новое`}</UDStatusBadge>}
          <div className="ml-auto flex items-center gap-3">
            <UDText type="caption-1" className="text-inkMuted tabular-nums">
              {dateStr} {timeStr}
            </UDText>
            {allowChange && (
              <div className="flex items-center gap-1">
                <UDIconButton
                  size="sm"
                  variant="ghost"
                  icon={<Pencil size={16} />}
                  tooltip={t`Редактировать`}
                  aria-label={t`Редактировать`}
                  onClick={() => onEdit?.(notification)}
                />
                <UDIconButton
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 size={16} />}
                  tooltip={t`Удалить`}
                  aria-label={t`Удалить`}
                  onClick={() => onDelete?.(notification.id)}
                />
              </div>
            )}
          </div>
        </div>
        <div className={NOTIFICATION_TEXT_BLOCK}>
          <UDText type="subhead" className="text-inkPrimary whitespace-pre-line">
            {notification.text}
          </UDText>
        </div>
      </div>
    </UDCard>
  );
};

export default NotificationComponent;
